/**
 * Downloadable Outlook draft (`.eml`) delivery for the operational email
 * templates (#434).
 *
 * The original delivery mechanism ({@link ./mailto.js}) pops a `mailto:` draft.
 * A `mailto:` body is **plain text only**, so the templates lose the
 * source-of-truth look-and-feel (bold field labels, headings, branding). This
 * module is the Option A replacement: it builds an RFC 5322 message with an
 * **HTML** body and downloads it as a `.eml` file. The `X-Unsent: 1` header is
 * the Outlook trick that makes the `.eml` open as an **editable compose draft**
 * (not a read-only received message), so the agent can review + send it exactly
 * as with the old `mailto:` flow — but now with formatting preserved.
 *
 * Like `mailto.ts` this is deliberately **client-side only**: the file is built
 * and downloaded in the browser (no PII leaves the app, nothing hits the BFF).
 * The same prefill discipline applies — pass whatever in-context detail we have
 * and leave the rest blank; **never** put a full PAN in a body (use
 * {@link maskCard}). Interpolated context values must be HTML-escaped by the
 * template's `buildHtml`.
 */

import type { EmailTemplate, TemplateContext } from './mailto.js';

/**
 * Escapes the five HTML-special characters so an interpolated context value
 * (customer name, account number, …) cannot inject markup into the HTML body.
 * Templates use this on every context value they interpolate into `buildHtml`.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * RFC 2047 "encoded-word" for a header value that contains non-ASCII
 * characters (Outlook needs this to render e.g. accented names in the Subject).
 * Pure-ASCII values are returned unchanged (fine for the POC).
 */
function encodeHeader(value: string): string {
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7F]*$/.test(value)) return value;
  const base64 = btoa(unescape(encodeURIComponent(value)));
  return `=?utf-8?B?${base64}?=`;
}

/**
 * Builds an RFC 5322 message string with an HTML body, ready to be saved as a
 * `.eml`. Line endings are normalised to CRLF (required by the format). The
 * `X-Unsent: 1` header makes Outlook open the file as an editable draft.
 */
export function buildEml(input: {
  to?: string;
  cc?: string;
  subject: string;
  html: string;
}): string {
  const headers: string[] = [];
  // X-Unsent MUST be present for Outlook to treat the .eml as a compose draft.
  headers.push('X-Unsent: 1');
  headers.push(`To: ${input.to ?? ''}`);
  if (input.cc) headers.push(`Cc: ${input.cc}`);
  headers.push(`Subject: ${encodeHeader(input.subject)}`);
  headers.push('MIME-Version: 1.0');
  headers.push('Content-Type: text/html; charset=utf-8');

  const message = `${headers.join('\r\n')}\r\n\r\n${input.html}`;
  return message.replace(/\r\n|\r|\n/g, '\r\n');
}

/**
 * #710 — UTF-8-safe base64 transport for the built `.eml` draft.
 *
 * On PTSB's BAU-SIT environment the POST carrying the draft is blocked at the
 * EDGE by the AWS WAF on their load balancer (HTTP 403 `server: awselb/2.0`,
 * HTML block page — the request never reaches the app): the WAF false-positives
 * on the raw HTML email markup in the JSON body. Carrying the draft as base64
 * of its **UTF-8 bytes** means the body holds only `[A-Za-z0-9+/=]`, so no email
 * markup crosses the edge, and the BFF decodes it strictly so the downloaded
 * `.eml` stays byte-identical. `TextEncoder` produces the UTF-8 bytes first
 * (a plain `btoa(eml)` would throw — or corrupt, once "fixed" via latin1 — on the
 * non-ASCII characters real drafts carry, e.g. accented names and the RFC 2047
 * `=?UTF-8?B?…?=` headers {@link buildEml} emits).
 */
export function encodeEmlBase64(eml: string): string {
  const bytes = new TextEncoder().encode(eml);
  return base64OfBytes(bytes);
}

/** Base64 (RFC 4648, browser btoa) of raw bytes, built byte-per-char so no
 * code-unit/latin1 corruption is possible. */
function base64OfBytes(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * #710 follow-up — gzip + base64 transport for the large templates.
 *
 * The MR !1057 base64 fix cleared the edge WAF's CONTENT-inspection
 * false-positive on email markup but not its SIZE rule: the 2026-08-29 triage
 * recorded the PTSB BAU-SIT WAF body-inspection limit at ~8 KB, and the five
 * templates @dylan219 reported still blocked on 2026-09-02 (EDS / EDL / BB /
 * MTG Data Breach + Travel spend) are exactly the templates whose base64 JSON
 * body exceeds it (≈14–29 KB encoded, vs ≤7 KB for every working template).
 * Gzip-first fixes the size rule: this repetitive Outlook HTML compresses
 * ~4–5x, so every template's encoded body fits under the cap while the bytes
 * the BFF finally serves stay **byte-identical** (gzip is lossless).
 *
 * The result goes in the dedicated `emlGzipBase64` field (the BFF magic-byte
 * sniffs the decoded bytes: `1f 8b 08` = gzip). Returns `undefined` when the
 * environment lacks the (widely supported: Chrome/Edge 80+, Firefox 113+,
 * Safari 16.4+) `CompressionStream` API or the stream errors — the caller then
 * falls back to plain {@link encodeEmlBase64}, i.e. pre-fix behaviour at worst
 * for the small templates, un-worse for the large ones.
 */
export async function encodeEmlGzipBase64(
  eml: string,
): Promise<string | undefined> {
  const CS = (
    globalThis as {
      CompressionStream?: new (format: 'gzip') => CompressionStream;
    }
  ).CompressionStream;
  if (typeof CS !== 'function') return undefined;
  try {
    const stream = new CS('gzip');
    const writer = stream.writable.getWriter();
    const reader = stream.readable.getReader();
    // Consume the readable CONCURRENTLY with the write — awaiting
    // write()/close() before reading deadlocks on payloads big enough to trip
    // the stream's backpressure (the 22 KB data-breach drafts).
    const drained = (async () => {
      const chunks: Uint8Array[] = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value as Uint8Array);
      }
      return chunks;
    })();
    await writer.write(new TextEncoder().encode(eml));
    await writer.close();
    const chunks = await drained;
    const total = chunks.reduce((n, c) => n + c.byteLength, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const c of chunks) {
      out.set(c, offset);
      offset += c.byteLength;
    }
    return base64OfBytes(out);
  } catch {
    return undefined;
  }
}

/**
 * Inverse of {@link encodeEmlBase64} — base64 → UTF-8 string, mirroring the
 * BFF's strict decode. Used by the unit/screen tests to assert the POSTed draft
 * without a lossy latin1 shortcut (`atob` alone would mangle non-ASCII).
 */
export function decodeEmlBase64(emlBase64: string): string {
  const binary = atob(emlBase64);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Inverse of {@link encodeEmlGzipBase64} — base64 → bytes → (gzip magic sniff,
 * mirroring the BFF) → UTF-8 string. Test seam only: asserts the POSTed gzip
 * draft without a Node-only `zlib` import in browser code.
 */
export async function decodeEmlGzipBase64(wire: string): Promise<string> {
  const binary = atob(wire);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  if (
    !(
      bytes.length >= 3 &&
      bytes[0] === 0x1f &&
      bytes[1] === 0x8b &&
      bytes[2] === 0x08
    )
  ) {
    // Same magic sniff as the BFF: no gzip header → plain bytes.
    return new TextDecoder().decode(bytes);
  }
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();
  // Read concurrently with the write — see the note in encodeEmlGzipBase64 on
  // stream backpressure at these sizes.
  const drained = (async () => {
    const chunks: Uint8Array[] = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value as Uint8Array);
    }
    return chunks;
  })();
  await writer.write(bytes);
  await writer.close();
  const chunks = await drained;
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return new TextDecoder().decode(out);
}

/**
 * Decodes the `.eml` out of a POST body sent to `/email-templates/eml`,
 * preferring `emlGzipBase64` then `emlBase64` — the BFF's field-preference
 * order. Test seam: screen tests assert the drafted content without pinning
 * which transport field the browser's CompressionStream produced.
 */
export async function decodeEmlDraftWire(body: {
  emlBase64?: string;
  emlGzipBase64?: string;
}): Promise<string> {
  if (typeof body.emlGzipBase64 === 'string') {
    return decodeEmlGzipBase64(body.emlGzipBase64);
  }
  if (typeof body.emlBase64 === 'string') {
    return decodeEmlBase64(body.emlBase64);
  }
  throw new Error('eml POST body carries no emlGzipBase64/emlBase64 field');
}

/**
 * #738 (@dylan219) — strips the `CIF: <digits>` phrase (case-insensitive;
 * `CIF:<digits>` / `CIF <digits>` tolerated) from a download filename stem.
 * Data-breach subjects carry the customer CIF (`Data Breach - CIF: 0000012345`,
 * `EDL: Data Breach - CIF: …`, …) and PTSB requires the saved `.eml` filename
 * not to include it. This applies to the FILENAME ONLY — the email subject and
 * body keep the CIF unchanged. Mirrored verbatim by the server guard
 * `sanitiseEmlFilename()` in `app/server/src/api.ts` so client and server
 * agree on the saved name.
 */
function stripCifForFilename(stem: string): string {
  return stem.replace(/\s*\bCIF\b\s*:?\s*\d+/gi, '');
}

/**
 * Turns a subject into a safe `.eml` filename: strips the CIF phrase (#738),
 * path/illegal characters, collapses whitespace and leftover dangling
 * separators (`-`/`:`) exposed by the CIF strip, trims length, and appends
 * `.eml`.
 */
export function emlFilename(subject: string): string {
  const base =
    stripCifForFilename(subject)
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^[\s:-]+|[\s:-]+$/g, '')
      .slice(0, 120) || 'email';
  return `${base}.eml`;
}

/** The signature of a `.eml` downloader seam. */
export type EmlDownloader = (
  filename: string,
  eml: string,
  tellerId?: string,
) => void;

/**
 * The BFF-served `.eml` downloader (#434 "Build option 2", @dylan219
 * 2026-07-23) — the robust path for downloads from the Genesys iframe.
 *
 * The app runs **embedded in a Genesys Cloud iframe** (§CLAUDE.md: we set
 * `Content-Security-Policy: frame-ancestors` for Genesys). A `.eml` produced
 * **in the browser** (a `message/rfc822` blob downloaded via a temporary
 * `<a download>` click, or a `window.open('', '_blank')` child that scripts an
 * anchor) is **blocked inside the Genesys iframe**: a download initiated from a
 * sandboxed cross-origin frame is refused unless the framing page's `sandbox`
 * includes `allow-downloads`, which we do not control. On Edge the previous
 * `window.open('', '_blank')` attempt just left an `about:blank` tab and saved
 * nothing (#434, @dylan219 2026-07-22 / 2026-07-23).
 *
 * @dylan219 approved **Option 2**: deliver the `.eml` as a **same-origin HTTP
 * attachment** from the BFF and let the browser's *download manager* handle the
 * save — that is not subject to the iframe download block. This is a two-step
 * flow:
 *
 *   1. POST the client-built `.eml` bytes to `/api/email-templates/eml` (the
 *      Teller-ID header rides on this authenticated `fetch`), encoded — gzip +
 *      base64 in `emlGzipBase64` (preferred, #710 follow-up size rule; plain
 *      base64 in `emlBase64` when the environment has no CompressionStream,
 *      #710) — so the edge WAF on PTSB's BAU-SIT load balancer, which
 *      false-positives on raw HTML email markup in the JSON body and 403s the
 *      request before it reaches the app, only ever sees base64, AND large
 *      drafts stay under its ~8 KB body-inspection limit (the five templates
 *      that still failed after !1057 were exactly the ones whose base64 body
 *      exceeded it — see {@link encodeEmlGzipBase64}); the BFF decodes it
 *      strictly and the download stays byte-identical. The BFF stashes
 *      them in the server-side session under a one-time token and returns it.
 *   2. Navigate a browsing context to `/api/email-templates/eml/{token}`, which
 *      responds with `Content-Disposition: attachment` — the download manager
 *      saves it. We open the download in a NEW window opened SYNCHRONOUSLY on the
 *      user gesture (before the async POST) so it is not popup-blocked; once the
 *      token is back we point that window at the download URL. If the popup was
 *      blocked (`window.open` returned null) we fall back to navigating a hidden
 *      same-origin `<iframe>` in the current document — a same-origin subframe
 *      navigation to an attachment is likewise handled by the download manager.
 *
 * The `.eml` bytes are still built entirely client-side (unchanged content /
 * masking); the BFF only re-delivers them, same-origin, no third party.
 *
 * Split out so {@link downloadTemplateEml} can be exercised in tests via an
 * injected seam (mirrors the `opener` seam in {@link openTemplateEmail}).
 */
export function bffDownload(
  filename: string,
  eml: string,
  tellerId?: string,
): void {
  // Open the download window synchronously on the user gesture so it is not
  // popup-blocked after the async POST. It briefly shows about:blank until we
  // point it at the attachment URL; the download manager then takes over and the
  // helper window closes itself (or the user closes the stray tab). `null` means
  // the popup was blocked — we fall back to a hidden same-origin iframe below.
  const win = window.open('', '_blank');
  // #710 follow-up — compress first (async; the whole flow below is already
  // promise-driven, so this never blocks the user gesture that opened `win`).
  // Import the generated client lazily so this module's builders can be imported
  // in a non-browser (node) test environment without evaluating the client's
  // module-top-level `location.origin`.
  void encodeEmlGzipBase64(eml)
    .then((emlGzipBase64) =>
      import('../api/client.js').then(({ api }) =>
        api.POST('/email-templates/eml', {
          params: { header: { 'X-Teller-Id': tellerId ?? '' } },
          // #710 — send ONLY an encoded form (no raw email markup in the body;
          // the raw `eml` field survives on the BFF as a compat fallback only).
          // #710 follow-up — PREFER the gzip+base64 form: the five templates
          // @dylan219 reported still blocked on BAU-SIT (EDS/EDL/BB/MTG Data
          // Breach + Travel spend) base64 to ≈14–29 KB bodies and trip the
          // edge WAF's ~8 KB body-inspection limit even though they are
          // base64-clean; gzip fits every template under the cap and the BFF
          // magic-byte-sniffs it. Plain base64 remains the fallback for
          // environments without CompressionStream.
          body:
            emlGzipBase64 !== undefined
              ? { filename, emlGzipBase64 }
              : { filename, emlBase64: encodeEmlBase64(eml) },
        }),
      ),
    )
    .then(({ data }) => {
      const token = data?.token;
      if (!token) {
        // Nothing to download — close the placeholder window we opened.
        try {
          win?.close();
        } catch {
          /* harmless */
        }
        return;
      }
      const url = `${location.origin}/api/email-templates/eml/${encodeURIComponent(token)}`;
      if (win) {
        // Point the (already-open) top-level window at the attachment URL — the
        // browser's download manager saves it and the helper window closes.
        win.location.href = url;
        setTimeout(() => {
          try {
            win.close();
          } catch {
            /* some browsers refuse; harmless — the download has fired */
          }
        }, 60_000);
        return;
      }
      // Popup blocked: navigate a hidden same-origin iframe instead. A
      // same-origin subframe navigation to a `Content-Disposition: attachment`
      // response is handled by the download manager (no `allow-downloads`
      // needed, because it is not a blob/`<a download>` click).
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.src = url;
      document.body.appendChild(frame);
      setTimeout(() => frame.remove(), 60_000);
    })
    .catch(() => {
      try {
        win?.close();
      } catch {
        /* harmless */
      }
    });
}

/**
 * Builds the `.eml` (HTML) draft for a template + context and downloads it, so
 * the agent's default mail client (Outlook) opens a pre-composed, **formatted**
 * editable draft. Returns the built `.eml` string (useful for tests / logging).
 *
 * Requires the template to provide a {@link EmailTemplate.buildHtml}; the
 * subject/recipients come from `build`/`to`/`cc`. The `downloader` seam
 * defaults to {@link bffDownload} (the BFF-served attachment path, #434 "Build
 * option 2") but is injectable so tests don't touch the network / navigation.
 * `tellerId` is forwarded to the downloader for the Teller-ID gated POST.
 */
export function downloadTemplateEml(
  template: EmailTemplate,
  ctx: TemplateContext,
  downloader: EmlDownloader = bffDownload,
  tellerId?: string,
): string {
  if (!template.buildHtml) {
    throw new Error(
      `Template "${template.id}" has no buildHtml; use openTemplateEmail (mailto) instead.`,
    );
  }
  const { subject } = template.build(ctx);
  const html = template.buildHtml(ctx);
  const eml = buildEml({ to: template.to, cc: template.cc, subject, html });
  downloader(emlFilename(subject), eml, tellerId);
  return eml;
}
